import { colors, shadows } from "@libertify/ui/theme-v2";

export type AnalyticsTheme = {
  // Datasets
  datasetBlue: string;
  datasetGreen: string;
  datasetAmber: string;
  datasetRed: string;
  datasetEmerald: string;
  datasetCoral: string;
  datasetCharcoal: string;
  datasetCyan: string;
  datasetViolet: string;
  datasetTeal: string;
  datasetTealMid: string;
  datasetTealDark: string;
  datasetTealDarker: string;
  datasetGrey: string;
  // Metric thresholds
  metricSuccess: string;
  metricWarning: string;
  metricOrange: string;
  metricError: string;
  metricDefault: string;
  // Card
  cardBorder: string;
  cardBg: string;
  cardShadow: string;
  barTrack: string;
  // Text
  textTitle: string;
  textBody: string;
  textSubtitle: string;
  textTick: string;
  textLabel: string;
  textMuted: string;
  textBarLabel: string;
  // Chart infrastructure
  gridLine: string;
  tooltipBg: string;
  tooltipBorder: string;
  pointBorder: string;
  /** Tile of a heatmap cell with no data. */
  heatEmpty: string;
};

export const analyticsTheme: AnalyticsTheme = {
  datasetBlue: "#4f7cf7",
  datasetGreen: "#83c182",
  datasetAmber: "#f59e0b",
  datasetRed: "#ef4444",
  datasetEmerald: "#34d399",
  datasetCoral: "#f87171",
  datasetCharcoal: "#1f2937",
  datasetCyan: "#06b6d4",
  datasetViolet: "#6d28d9",
  datasetTeal: colors.accent.base,
  datasetTealMid: colors.accent.active,
  datasetTealDark: "#0f766e",
  datasetTealDarker: "#134e4a",
  datasetGrey: "#b8bdc9",
  metricSuccess: "#22c55e",
  metricWarning: "#eab308",
  metricOrange: "#f97316",
  metricError: "#ef4444",
  metricDefault: "#000",
  cardBorder: "#dbdfe0",
  cardBg: "#ffffff",
  cardShadow: shadows.elev1,
  barTrack: "#f3f4f6",
  textTitle: "#333",
  textBody: "#666",
  textSubtitle: "#888",
  textTick: "#999",
  textLabel: "#6b7280",
  textMuted: "#9ca3af",
  textBarLabel: "#fff",
  gridLine: "rgba(0, 0, 0, 0.06)",
  tooltipBg: "#fff",
  tooltipBorder: "#e5e7eb",
  pointBorder: "#fff",
  heatEmpty: colors.surface.muted,
};

export const useAnalyticsTheme = (): AnalyticsTheme => analyticsTheme;

/** Short color key that drives an accent bar/border, value text and icon/tile tint. */
export type StatCardColor = "green" | "amber" | "red" | "blue";

export type StatCardPalette = { accent: string; value: string; iconBg: string };

/** Static accent colors shared by `StatCard` and `MetricTile`. "green" is resolved from the org theme instead. */
export const STATIC_PALETTES: Record<
  Exclude<StatCardColor, "green">,
  StatCardPalette
> = {
  amber: { accent: "#f59e0b", value: "#f97316", iconBg: "#fef4e6" },
  red: { accent: "#ef4444", value: "#ef4444", iconBg: "#fdeaea" },
  blue: { accent: "#6366f1", value: "#6366f1", iconBg: "#ecedfe" },
};

export type HeatShade = { background: string; color: string };

/** Heatmap tiles, from the worst reading to the best. */
export const HEAT_SHADES = {
  worst: { background: "#fca5a5", color: "#7f1d1d" },
  bad: { background: "#fee2e2", color: "#7f1d1d" },
  neutral: { background: analyticsTheme.barTrack, color: "#374151" },
  good: { background: "#ccfbf1", color: analyticsTheme.datasetTealDarker },
  better: { background: colors.accent.border, color: "#083344" },
  best: { background: analyticsTheme.datasetTealDark, color: colors.text.onAccent },
} satisfies Record<string, HeatShade>;
